import { knoxApi } from '@/api/knox/knoxApi';
import { type KnoxDevice } from '@/models/knox/KnoxDevice';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { fillHeight } from '../../theme';

const STATUS_OPTIONS = ['Active', 'Inactive', 'NotEnrolled', 'Expired', 'FailedToEnroll'];
const BATTERY_OPTIONS = ['Good', 'Normal', 'Weak', 'Bad'];

// Battery state-of-health → chip color.
const batteryColor = (soh: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (soh) {
        case 'Good': return 'success';
        case 'Normal': return 'info';
        case 'Weak': return 'warning';
        case 'Bad': return 'error';
        default: return 'default';
    }
};

const DeviceHealth = () => {
    const app = useAppContext();
    const strings = useLocalizedStrings();
    const brandId = app.brandId ?? undefined;

    const [statuses, setStatuses] = useState<string[]>([]);
    const [batterySohs, setBatterySohs] = useState<string[]>([]);

    const { data, isPending } = useQuery({
        queryKey: ['knox-devices', brandId, statuses, batterySohs],
        enabled: !!brandId,
        queryFn: () => knoxApi.listDevices(brandId!, { statuses, batterySohs }),
        staleTime: 60_000,
    });

    const columns: GridColDef<KnoxDevice>[] = [
        { field: 'serialNumber', headerName: strings.serialNumber, flex: 1.4 },
        { field: 'model', headerName: strings.model, flex: 1 },
        { field: 'firmwareVersion', headerName: strings.firmware, flex: 1.2 },
        { field: 'osVersion', headerName: strings.osVersion, flex: 1 },
        {
            field: 'batterySoh',
            headerName: strings.batteryHealth,
            flex: 1,
            renderCell: (p: GridRenderCellParams<KnoxDevice, string>) =>
                p.value ? <Chip size="small" color={batteryColor(p.value)} label={p.value} /> : null,
        },
        { field: 'status', headerName: strings.status, flex: 1 },
    ];

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{strings.deviceHealth}</Typography>

            {!brandId ? (
                <Typography color="text.secondary">{strings.selectBrand}</Typography>
            ) : (
                <>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="knox-status-label">{strings.status}</InputLabel>
                            <Select
                                labelId="knox-status-label"
                                multiple
                                value={statuses}
                                onChange={(e) => setStatuses(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                input={<OutlinedInput label={strings.status} />}
                                renderValue={(selected) => selected.join(', ')}
                            >
                                {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="knox-battery-label">{strings.batteryHealth}</InputLabel>
                            <Select
                                labelId="knox-battery-label"
                                multiple
                                value={batterySohs}
                                onChange={(e) => setBatterySohs(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                input={<OutlinedInput label={strings.batteryHealth} />}
                                renderValue={(selected) => selected.join(', ')}
                            >
                                {BATTERY_OPTIONS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <DataGrid
                            getRowId={(row) => row.deviceId || row.serialNumber}
                            rows={data?.devices ?? []}
                            columns={columns}
                            autoPageSize
                            loading={isPending}
                            localeText={{ noRowsLabel: strings.devicesNotFound }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default DeviceHealth;
